import { format, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { isEqual } from "lodash";
import { showToast } from "@int-traderjoes/ui-components-library";
import { TreeNodeType, DropdownItem } from "@/context/data.types";
import {
  AvailableTransitions,
  AvailableTransitionsObj,
  AvailableTransitionsShort,
} from "@/types/ProductService.types";
import Constants from '@/static/constants/constants.json';
import VendorService from "@/services/vendorService";
import dayjs from "dayjs";
import FileService from "@/services/fileService";
import ProductService from "@/services/productService";
import { handleApiError } from "@/hooks/useApiErrorHandler";
/**
 * Maps the active info form index to the corresponding process stage for comments
 * @param activeInfoFormIndex - The current active tab index
 * @returns The process stage string to be used in comment payloads
 */
export function getProcessStageByTab(activeInfoFormIndex: number): string {
  const tabToProcessStageMap: { [key: number]: string } = {
    0: "shoppinglist",        // Product Information tab
    1: "vendorassessment",    // Vendor Information tab
    2: "prepanel",            // Pre Panel Information tab
    3: "businessreview",      // Business Review tab
    4: "tastingpanel"         // Tasting Panel tab
  };

  return tabToProcessStageMap[activeInfoFormIndex] || "shoppinglist";
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")?.[1] ?? ""; // Extract only the data part
      resolve(base64Data);
    };

    reader.onerror = error => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

// Function to get more generic file type
export function getFileCategory(mimeType: string): string {
  if (mimeType.startsWith("image/")) {
    return "image";
  } else if (mimeType.startsWith("audio/")) {
    return "audio";
  } else if (mimeType.startsWith("video/")) {
    return "video";
  } else if (mimeType.startsWith("application/")) {
    return "document";
  } else if (mimeType.startsWith("text/")) {
    return "text";
  } else {
    return "other";
  }
}


// Function to convert a Date object or ISO 8601 string to mm/dd/yyyy format
export const formatToMMDDYYYY = (date: string | Date): string => {
  // Parse the date (if it's a string, convert it to Date object)
  let parsedDate = typeof date === "string" ? new Date(date) : date;
  
  if(isNaN(parsedDate.getTime())) return "";

  // Format the Date object to mm/dd/yyyy format
  return format(parsedDate, "MM/dd/yyyy");
};

// Convert the date into UTC and format it
export const convertToUTCAndFormat = (date: Date): string => {
  return formatInTimeZone(date, "UTC", "MM/dd/yyyy HH:mm:ss");
};

// Converts 'MM/dd/yyyy HH:mm:ss' UTC string to local time and formats
export const convertUTCStringToLocal = (input: string): string => {
  const [datePart, timePart] = (input ?? "").split(" ");
  if (!datePart || !timePart) return input ?? "";
  const [month, day, year] = datePart.split("/") ?? [];
  const [hours, minutes, seconds] = timePart.split(":") ?? [];
  if (!month || !day || !year || !hours || !minutes || !seconds) return input ?? "";

  // Construct UTC Date
  const utcDate = new Date(
    Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds)
    )
  );

  // Format using system's local time zone
  return format(utcDate, "MM/dd/yyyy HH:mm:ss");
};


// To format date in month-day-year
export function reformatDateAsMDY(dateString: string): string {
  // Regex to check if it's already in MM-DD-YYYY format
  const mdyFormat = /^\d{2}-\d{2}-\d{4}( \d{2}:\d{2}:\d{2})?$/;

  if (mdyFormat.test(dateString)) {
    return dateString; // Already formatted, return as is
  }

  let [datePart, timepart] = dateString.split(" ");
  if (!datePart) return dateString;
  if (!timepart) {
    timepart = "00:00:00";
  }

  const [year, month, day] = datePart.split("-");
  return `${month}-${day}-${year} ${timepart}`;
}

export { decodeHtmlEntities } from "./decodeHtmlEntities";

/**
 * Converts API-stored escaped newline sequences into real line breaks for display.
 * Handles legacy values where multiline text was persisted as literal `\n` characters.
 */
export function normalizeEscapedNewlines(value: string): string {
  return value.replaceAll(/\\r\\n|\\n|\\r/g, "\n");
}

export function normalizeEscapedNewlinesIfString(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return normalizeEscapedNewlines(value);
}

/** For API text fields (e.g. comments) where non-strings should render as empty. */
export function normalizeEscapedNewlinesFromApi(value: unknown): string {
  if (typeof value !== "string") return "";
  return normalizeEscapedNewlines(value);
}

/** Converts input text to uppercase before storing in state. */
export function normalizeUppercaseText(value: unknown): string {
  return String(value ?? "").toUpperCase();
}

export const RequiredDocumentsValidateFile = (file: File, validationMessages?: any, customFileTypeError?: string, allowedTypes: string[] = ["application/pdf"]): boolean => {
  const fileSizeMB = file?.size / (1024 * 1024); // Convert bytes to MB
  const fileSizeKB = file?.size / 1024; // Convert bytes to KB
  
  if (fileSizeKB < 1 || fileSizeMB > 40) {
    showToast({
      type: "error",
      message: validationMessages?.FILE_SIZE_BETWEEN_1_AND_40MB_ERROR || "File size must be between 1MB and 40MB.",
    });
    return false;
  }

  // If allowedTypes is empty or contains "*", allow all types
  if (allowedTypes.length === 0 || allowedTypes.includes("*")) {
    return true;
  }
  if (!allowedTypes.includes(file?.type)) {
    showToast({
      type: "error",
      message: customFileTypeError || validationMessages?.ONLY_NDA_PDF_ALLOWED_ERROR || "Only PDF files are allowed.",
    });
    return false;
  }

  return true;
};

export const IngredientDocumentsValidateFile = (file: File, validationMessages?: any): boolean => {
  const fileSizeMB = file?.size / (1024 * 1024); // Convert bytes to MB
  const fileSizeKB = file?.size / 1024; // Convert bytes to KB

  if (fileSizeKB < 1 || fileSizeMB > 40) {
    showToast({
      type: "error",
      message: validationMessages?.FILE_SIZE_BETWEEN_1_AND_40MB_ERROR || "File size must be between 1MB and 40MB.",
    });
    return false;
  }

  // Allow all file types for ingredients as per requirement
  return true;
};

/**
 * Formats API date-time strings for display in the user's local timezone.
 * Backend sends `MM/dd/yyyy HH:mm:ss` in UTC (see My Tasks filter/sort).
 */
export function formatDateToCustomString(inputDate: string): string {
  const utcDate = parseApiUtcDateTimeString(inputDate);
  if (!utcDate) {
    const trimmed = String(inputDate ?? "").trim();
    return trimmed;
  }

  return format(utcDate, "MMMM dd, yyyy");
}

/** Parses `MM/dd/yyyy HH:mm:ss` API values stored in UTC. */
export function parseApiUtcDateTimeString(input: string): Date | null {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) return null;

  const utcDate = new Date(`${trimmed} UTC`);
  if (!Number.isNaN(utcDate.getTime())) {
    return utcDate;
  }

  const [datePart, timePart] = trimmed.split(" ");
  if (!datePart || !timePart) return null;
  const [month, day, year] = datePart.split("/") ?? [];
  const [hours, minutes, seconds] = timePart.split(":") ?? [];
  if (!month || !day || !year || !hours || !minutes || !seconds) return null;

  const parsed = new Date(
    Date.UTC(
      Number.parseInt(year, 10),
      Number.parseInt(month, 10) - 1,
      Number.parseInt(day, 10),
      Number.parseInt(hours, 10),
      Number.parseInt(minutes, 10),
      Number.parseInt(seconds, 10)
    )
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export const delay = (delayTimeS: number, oldTimer?: any) => {
  let rejectFn: (value: boolean) => void;
  let timer: any;
  if (oldTimer) {
    oldTimer.cancel();
  }
  const promise = new Promise((resolve, reject) => {
    rejectFn = reject;
    timer = setTimeout(() => {
      console.log("timeout completed", timer);
      resolve(true);
    }, 1000 * delayTimeS); // Save the form after defined inactivity time
    console.log("setting timeout", timer);
  });

  return {
    promise,
    cancel: () => {
      clearTimeout(timer);
      rejectFn(false);
    },
  };
};

// Function to remvoe any particular field from the payload
export const omitFields = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  if (obj == null || typeof obj !== 'object') {
    return {} as Omit<T, K>;
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K))
  ) as Omit<T, K>;
};

// Function to remove all the fields which are empty
// export const removeEmptyValues = (obj: Record<string, any>) => {
//   return Object.fromEntries(
//     Object.entries(obj)
//       .filter(([key, value]) => {
//         // Remove fields with empty values, null, undefined, or empty objects
//         return value !== "" && value !== null && value !== undefined && !(typeof value === 'object' && Object.keys(value).length === 0);
//       })
//   );
// };

// Function to fetch the file form url
export async function fetchFileFromUrl(blobUrl: string, fileName: string): Promise<File> {
  try {
    // Fetch the file as a blob
    const response = await fetch(blobUrl);
    const blob = await response.blob();

    // Convert blob to File
    return new File([blob], fileName, { type: blob.type });
  } catch (error) {
    console.error("Error fetching file:", error);
    throw error;
  }
}

export function removeParenthesisContent(str: string) {
  const regex = / *\([^)]*\)/g; // NOSONAR
  return str.replace(regex, "").trim();
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/; // /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // NOSONAR
  return emailRegex.test(email);
}

// Function for validating phone number
export function validatePhoneNumber(phone: string): boolean {
  if (phone === "") return true;
  const phoneRegex = /^[\d\s\-\+\(\)]*$/;
  return phoneRegex.test(phone) && phone.length <= 15;
}

/** Empty zip is valid here; mandatory checks are handled separately. Shows toast when too short. */
export function validateZipCodeMinLengthOnBlur(
  zip: string | null | undefined,
  languageText?: any
): boolean {
  const zipValue = String(zip ?? "").trim();
  if (!zipValue || zipValue.length >= Constants.ZIP_CODE_MIN_LENGTH) return true;

  showToast({
    type: "error",
    message:
      languageText?.MESSAGES?.VALIDATION_MESSAGES?.ZIP_CODE_MIN_LENGTH_ERROR ||
      languageText?.VALIDATION_MESSAGES?.ZIP_CODE_MIN_LENGTH_ERROR ||
      `Zip code must be at least ${Constants.ZIP_CODE_MIN_LENGTH} characters long.`,
  });
  return false;
}

// Function for getting profile initials from full name
export const getProfileInitials = (fullName: string): string => {
  if (!fullName) return "NA"; // Default initials if no name is provided

  const nameBeforeParen = fullName.indexOf("(") > -1 ? (fullName.split("(")?.[0] ?? "") : fullName;
  const nameParts = nameBeforeParen.trim().split(" ");

  if (nameParts.length === 1) {
    // If it's a single word, take the first two letters
    const first = nameParts?.[0] ?? "";
    return `${first?.[0]?.toUpperCase() ?? ""}${first?.[1]?.toUpperCase() ?? ""}` || "NA";
  }

  const firstName = nameParts?.[0] ?? ""; // Get the first part (first name)
  const lastName = nameParts?.[nameParts.length - 1] ?? ""; // Get the last part (last name)

  const initials = `${firstName?.[0]?.toUpperCase() ?? ""}${lastName?.[0]?.toUpperCase() ?? ""}`; // Combine initials of first and last name
  return initials || "NA"; // Return initials or a default value if no valid initials
};

/** Display name shown in the profile menu (same logic as ProfileMenu). */
export function getProfileDisplayName(fullName: string): string {
  if (!fullName) return "";

  const nameParts =
    fullName.indexOf("(") > -1
      ? (fullName.split("(G10X")?.[0] ?? "").trim().split(" ")
      : fullName.trim().split(" ");

  return nameParts.filter(Boolean).join(" ");
}

// Function to convert the utc date and time to local date and time
export function convertUtcToLocalDateTime(utcDateString: string): string {
  // Create a Date object in UTC
  const utcDate = new Date(utcDateString + " UTC");

  // Format the date in the browser's local timezone
  return utcDate.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false, // Ensures 24-hour format
  });
}

// Function to convert the UTC date to local date (without time)
export function convertUtcToLocalDate(utcDateString: string): string {
  // Create a Date object in UTC
  const utcDate = new Date(utcDateString + " UTC");

  // Format the date in the browser's local timezone (only date, no time)
  return utcDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export const capitalizeText = (text: string): string => {
  if (!text) return "";
  // Trim the text and capitalize the first letter of the first word only
  // const trimmedText = text.trim().toLowerCase();
  // return trimmedText.charAt(0).toUpperCase() + trimmedText.slice(1);
  return text;
};

/**
 * Normalizes a value by splitting on spaces or underscores, trimming parts,
 * lowercasing them, then joining with a single space.
 */
export function getGenericValueCase(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";

  return str
    .split(/[ _]+/)
    .map(part => part.trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
}

// form events needs to write interface/ parent class

export const handleInputChange =
  (field: string, setFormData: React.SetStateAction<any>) =>
  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [field]: e.target.value,
    }));
  };

/**
 * Sanitizes vendor corporate name to prevent characters that could break base64 encoding/decoding
 * Blocks characters: < > " ' / \ = which can cause issues with JSON/base64 encoding
 * @param value - The input value to sanitize
 * @returns The sanitized value with invalid characters removed
 */
export const sanitizeVendorCorporateName = (value: string): string => {
  // Block characters that are not valid in base64: only allow A-Z, a-z, 0-9, spaces, and common punctuation
  // Specifically block: < > " ' / \ = which can cause issues with JSON/base64 encoding
  return value
    .replace(/[<>]/g, '') // Remove < > 
    .replace(/[\\/]/g, '') // Remove \ and /
    .replace(/=/g, '')// Remove =
    .replace(/:/g, ''); // Remove :
};

/**
 * Removes hidden/non-printable characters (e.g., zero-width space) and trims.
 * Intended for sanitizing outgoing API payload strings.
 */
export const sanitizeStringForPayload = (value: string): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  // Zero-width chars + BOM + word joiner
  const withoutZeroWidth = str.replace(/[\u200B-\u200D\uFEFF\u2060]/g, "");
  // Non-printable control chars (keep \t, \n, \r)
  const withoutControls = withoutZeroWidth.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, "");
  return withoutControls.trim();
};

/**
 * Deep-sanitizes an object/array/string payload by trimming all strings and removing hidden characters.
 * NOTE: This is a pure function (does not mutate input).
 */
export const sanitizePayloadForApi = <T,>(input: T): T => {
  // primitives
  if (typeof input === "string") return sanitizeStringForPayload(input) as any;
  if (input === null || input === undefined) return input;
  if (typeof input !== "object") return input;

  // preserve special objects
  if (input instanceof Date) return input;
  if (typeof File !== "undefined" && input instanceof File) return input;

  // arrays
  if (Array.isArray(input)) {
    return (input as any[]).map((v) => sanitizePayloadForApi(v)) as any;
  }

  // plain objects
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input as any)) {
    out[k] = sanitizePayloadForApi(v);
  }
  return out as T;
};

// New helper for consolidated state pattern (partial updates)
export const handleInputChangePartial =
  (field: string, updateFunction: (data: any) => void) =>
  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateFunction({
      [field]: e.target.value,
    });
  };

export const handleInputChangeMultipleForm =
  (field: string, setFormData: React.SetStateAction<any>,index: number) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prevForms:any) => {
            const updatedForms = [...prevForms];
            updatedForms[index] = {
              ...updatedForms[index],
              [field]: e.target.value,
            };
            return updatedForms;
          });
    };

export const handleNumberChange =
  (field: string, setFormData: React.SetStateAction<any>) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!isNaN(Number(value)) || value === "") {
      setFormData((prevData: any) => ({
        ...prevData,
        [field]: value,
      }));
    }
  };
export const handleCheckboxChange =
  (field: string, setFormData: React.SetStateAction<any>) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [field]: e.target.checked,
    }));
  };
export const toggleGeneralForm = (setIsFormExpanded: React.SetStateAction<any>) => {
  setIsFormExpanded((prev: any) => !prev);
};

/** True when Dropdown / DropdownAutoSuggest signals an X-clear. */
export const isDropdownClearEvent = (option: any): boolean =>
  option == null || option.value === "" || option.value == null;

/**
 * Map allowlisted empty/null clearable fields to "" so JSON keeps the key.
 * BE clears selects on "" (null is ignored). Walks nested idea / vendorAssessment payloads.
 */
export const normalizeClearableFieldsToNull = <T,>(
  obj: T,
  clearableFields: readonly string[]
): T => {
  if (obj == null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => normalizeClearableFieldsToNull(item, clearableFields)) as T;
  }

  const next: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj as Record<string, any>)) {
    if (
      clearableFields.includes(key) &&
      (value === "" || value === undefined || value === null)
    ) {
      next[key] = "";
    } else if (value != null && typeof value === "object") {
      next[key] = normalizeClearableFieldsToNull(value, clearableFields);
    } else {
      next[key] = value;
    }
  }
  return next as T;
};

export const handleDropdownChange =
  (field: string, setFormData: React.SetStateAction<any>) =>
  (e: React.ChangeEvent<HTMLInputElement> | null, option: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      // null (not undefined/"") so save payloads can send an explicit clear
      [field]: isDropdownClearEvent(option) ? null : option.value,
    }));
  };

export const handleDropdownChangeMultipleForms =
  (field: string, setFormData: React.SetStateAction<any>, index: number) =>
  (e: React.ChangeEvent<HTMLInputElement>, option: any) => {
    setFormData((prevForms: any) => {
      const updatedForms = [...prevForms];
      updatedForms[index] = {
        ...updatedForms[index],
        [field]: option?.value ?? "",
      };
      return updatedForms;
    });
  };

export const handleDateChange =
  (field: string, setFormData: React.SetStateAction<any>) => (date: Date | null) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
    setFormData((prevData: any) => ({
      ...prevData,
      [field]: formattedDate,
    }));
  };

export const getMandatoryFields = (formData: any) => {
  const mandatoryFields = Object.keys(formData).filter(key => {
    return formData[key].isMandatory === true;
  });
  return mandatoryFields;
};

export const getNodePath = (
  data: TreeNodeType[],
  selectedId: string
): { id: string; name: string }[] => {
  const map: { [key: string]: TreeNodeType } = {};

  const flatten = (nodes: TreeNodeType[]) => {
    for (let node of nodes) {
      map[node.id] = node;
      if (node.children) flatten(node.children);
    }
  };

  flatten(data);

  const path: { id: string; name: string }[] = [];

  let current = map[selectedId];
  while (current) {
    path.unshift({ id: current.id, name: current.name });
    current = map[current.parentId];
  }

  return path;
};

export const isValidTwoDecimalNumber = (value: string): boolean => {
  const validNumberRegex = /^\d*\.?\d{0,2}$/;
  return value === "" || validNumberRegex.test(value);
};

/**
 * Validates retail price input format
 * Allows max 3 digits before decimal (max 999) and up to 2 decimal places
 * Valid: 0, 0., 0.0, 0.00, 100, 100., 100.0, 100.00, 999.99, .5, .99
 * Invalid: 1000 (>3 digits before decimal), 99.999 (>2 digits after decimal)
 */
export const isValidRetailPriceInput = (value: string): boolean => {
  // Allow empty string
  if (value === '') return true;
  
  // Allow single decimal point
  if (value === '.') return true;
  
  // Allow numbers with max 3 digits before decimal and up to 2 decimal places
  return /^(?:\d{1,3}(?:\.\d{0,2})?|\.\d{1,2})$/.test(value);
};

/** Returns sanitized retail price, or null if the keystroke should be rejected. */
export const sanitizeRetailPriceFieldInput = (
  currentValue: string,
  nextValue: string
): string | null => {
  if (currentValue.length > 0) {
    const isDeletion = nextValue.length < currentValue.length;
    if (!isDeletion && !isValidRetailPriceInput(nextValue)) {
      return null;
    }
    return nextValue;
  }
  return isValidRetailPriceInput(nextValue) ? nextValue : null;
};

/** Digits-only input (e.g. case pack / packSize). Returns null if invalid. */
export const sanitizeDigitsOnlyFieldInput = (nextValue: string): string | null =>
  /^\d*$/.test(nextValue) ? nextValue : null;

//format dropdown options with capitalizeText
export const formatDropdownOptions = (
  data: DropdownItem[], // Array of objects
  labelKey: string = "description", // Default 'description' as the label key
  valueKey: string = "id", // Default 'id' as the value key
  disabled: boolean = false
): any => {
  // Return type is an array of label-value objects
  if (!Array.isArray(data)) {
    return [];
  }
  const formattedOptions = [
    ...data.map(item => ({
      label: capitalizeText(item[labelKey]), // Apply capitalizeText function
      value: item[valueKey], // Dynamically use the key eg; value -> 47
      disabled: disabled,
    })),
  ];
  return formattedOptions;
};

export const formatDropdownWithPendingOptions = (
  data: DropdownItem[], // Array of objects
  labelKey: string = "description", // Default 'description' as the label key
  valueKey: string = "id", // Default 'id' as the value key
  disabled: boolean = false,
  isPending: boolean = false
): Array<{label: string, value: string, disabled: boolean, isPending: boolean}> => {
  // Return type is an array of label-value objects with isPending: true
  if (!Array.isArray(data)) {
    return [];
  }
  const formattedOptions = data.map(item => ({
    label: item[labelKey],
    value: item[valueKey],
    disabled: disabled,
    isPending: item.isPending === undefined ? isPending : item.isPending
  }));
  return formattedOptions;
};

export const formatRawDropdownOptions = (
  data: DropdownItem[], // Array of objects
  labelKey: string = "description", // Default 'description' as the label key
  valueKey: string = "id", // Default 'id' as the value key
  disabled: boolean = false
): any => {
  // Return type is an array of label-value objects
  if (!Array.isArray(data)) {
    return [];
  }
  const formattedOptions = [
    ...data.map(item => ({
      label: item[labelKey], // Apply capitalizeText function
      value: isNaN(Number(item[valueKey])) ? item[valueKey] : Number(item[valueKey]), // Dynamically use the key eg; value -> 47
      disabled: disabled,
    })),
  ];
  return formattedOptions;
};

// Preserve original value type without any numeric conversion
export const formatRawDropdownOptionsPreserve = (
  data: DropdownItem[], // Array of objects
  labelKey: string = "description", // Default 'description' as the label key
  valueKey: string = "id" // Default 'id' as the value key
): any => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map(item => ({
    label: item[labelKey],
    value: item[valueKey],
  }));
};

/**
 * Converts an array of values to MultipleSelect format [{label, value}]
 * Used to transform stored string values into the object format expected by MultipleSelect component
 * @param values - Array of string values, single value, or null/undefined
 * @param options - Dropdown options array with label/value structure
 * @returns Array of {label, value} objects for MultipleSelect component
 */
export const formatValuesForMultipleSelect = (
  values: any,
  options: Array<{label: string, value: any}>
): Array<{label: string, value: string}> => {
  const arr = Array.isArray(values) ? values : [];
  return arr.map((val: any) => {
    // Handle if val is already an object (extract the actual value)
    const searchValue = typeof val === 'object' ? (val?.id ?? val?.value ?? val) : val;
    // Find matching option by comparing values as strings
    const match = options.find((o: any) => String(o?.value) === String(searchValue));
    // Return matched option or create fallback with the value as both label and value
    return match || { label: String(searchValue), value: String(searchValue) };
  });
};

// Format dropdown options with tooltip support (for ingredient types with enhanced fields)
export const formatDropdownOptionsWithTooltip = (
  data: Array<{id: string, name: string, toolTipMessage?: string, requiredDocument?: string, tooltip?: string}>,
  labelKey: string = "name",
  valueKey: string = "id"
): Array<{value: string | number, label: string, toolTipMessage?: string, requiredDocument?: string, tooltip?: string}> => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map(item => {
    // Use proper property access instead of dynamic indexing
    const label = labelKey === "name" ? item.name : item.id;
    const value = valueKey === "id" ? item.id : item.name;
    const numericValue = isNaN(Number(value)) ? value : Number(value);
    
    return {
      label,
      value: numericValue,
      // Include new fields from API response
      ...(item.toolTipMessage && { toolTipMessage: item.toolTipMessage }),
      ...(item.requiredDocument && { requiredDocument: item.requiredDocument }),
      // Keep backward compatibility with old tooltip field
      ...(item.tooltip && { tooltip: item.tooltip })
    };
  });
};

export const formatDropdownOptionsCM = (
  data: DropdownItem[], // Array of objects
  labelKey: string = "description", // Default 'description' as the label key
  valueKey: string = "id", // Default 'id' as the value key
  optionalKey: string = "category"
): any => {
  // Return type is an array of label-value objects
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map(item => {
    const isCategoryManagerUser = optionalKey === "category";

    return {
      label: capitalizeText(item[labelKey]),
      value: isCategoryManagerUser ? Number(item[valueKey]) : item[valueKey],
      // ...(optionalKey ? { [optionalKey]: item[optionalKey] } : {}),
    };
  });
};

// tab related functions
export function a11yProps(index: number) {
  return {
    id: `vendor-info-tab-${index}`,
    "aria-controls": `vendor-info-tabpanel-${index}`,
  };
}
export const handleAllErrorFields = (
  allMadatoryFieldErrors: any,
  setWorkflowButtons: React.Dispatch<React.SetStateAction<any>> | undefined
) => {
  if (allMadatoryFieldErrors == null || typeof allMadatoryFieldErrors !== "object") {
    return;
  }
  const allFalse = Object.values(allMadatoryFieldErrors).every(
    value => typeof value === "boolean" && value === false
  );
  if (allFalse) {
    setWorkflowButtons?.((prevState: any) => {
      return {
        ...prevState,
        proceedToVA: {
          ...prevState.proceedToVA,
          disabled: false,
        },
        save: {
          ...prevState.save,
          disabled: false,
        },
        requestForRevision: {
          ...prevState.requestForRevision,
          disabled: false,
        },
        reviewComplete: {
          ...prevState.reviewCompleted,
          disabled: false,
        },
        shareForReview: {
          ...prevState.shareForReview,
          disabled: false,
        },
      };
    });
  }
};

// Function to replace objects with their IDs in a nested object
export function replaceObjectsWithIds(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const key in obj) {
    const value = obj[key];

    if (
      typeof value === "object" &&
      value !== null &&
      "id" in value &&
      "value" in value &&
      Object.keys(value).length === 2
    ) {
      result[key] = value.id;
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      result[key] = replaceObjectsWithIds(value); // nested objects
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function convertFieldsToIds<T extends Record<string, any>>(
  inputObj: T,
  fieldsToConvert: string[]
): T {
  const result: Record<string, any> = { ...inputObj };

  for (const field of fieldsToConvert) {
    if (result[field] && typeof result[field] === "object" && "id" in result[field]) {
      result[field] = result[field].id;
    }
  }

  return result as T;
}

// Comapre two objects deeply
export const isDeepEqual = (obj1: any, obj2: any) => {
  console.log("Comparing objects:", obj1, obj2, isEqual(obj1, obj2));
  return isEqual(obj1, obj2);
};

// Get workflow transitionid
export function getWorkflowTransitionIdByLabel(
  availableTransitions: AvailableTransitionsObj | null | undefined,
  targetLabel: string
): string | undefined {
  const transitionsData =
    (availableTransitions as AvailableTransitions)?.transitions?.data ??
    (availableTransitions as AvailableTransitionsShort)?.data;

  return transitionsData?.find(transition => transition.label === targetLabel)?.transitionId;
}

export function removeEmptyValues(obj: any, skipFields: string[] = []): any {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(item => removeEmptyValues(item, skipFields))
      .filter(item => !isEmpty(item));
    return cleanedArray;
  }

  if (typeof obj === "object" && obj !== null) {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip fields that are in the skipFields array
      if (skipFields.includes(key)) {
        newObj[key] = value;
        continue;
      }

      const cleanedValue = removeEmptyValues(value, skipFields);
      if (!isEmpty(cleanedValue)) {
        newObj[key] = cleanedValue;
      }
    }
    return newObj;
  }

  return obj;
}

export function isEmpty(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    value === 0 ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && value !== null && Object.keys(value).length === 0)
  );
}

// Function to check whether all the field are empty , null or undefined.
export const isEmptyObject = (obj: Record<string, any>): boolean => {
  if (!obj || typeof obj !== "object") return false;

  const cleaned = removeEmptyValues(obj) || {};
  return Object.values(cleaned).length === 0;
};

/**
 *  Checks if the given string can be converted to number if not defaults to null
 * @param num
 * @returns
 */
export const isNumber = (num: string): number | null => {
  if (!num) {
    return null;
  }
  return isNaN(Number(num)) ? null : Number(num);
};

/** Empty input → null; user-entered 0 → 0 (for save payloads). */
export function nullIfEmptyPayloadNumber(
  value: string | number | null | undefined
): number | null {
  if (value === "" || value == null) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = String(value).trim();
  if (trimmed === "") {
    return null;
  }
  const normalized = trimmed.replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

export const getFormatedStatus = (productStatus:string):string => {
  return productStatus?.replaceAll(" ", "_").toLowerCase()
}



//TODO: This handles tasting panel tab status check should be refactored to a generalised approach
export const tastingPanelStatusMap: string[] = [
  getFormatedStatus(Constants?.READY_FOR_TASTING_PANEL),
  getFormatedStatus(Constants?.NEEDS_REVISION_STATUS),
  getFormatedStatus(Constants?.TASTING_PANEL_APPROVED)
]

//TODO: This handles prepanel tab status check should be refactored to a generalised approach
export const prePanelProductStatusMap: string[] = [
  ...tastingPanelStatusMap,
  //getFormatedStatus(Constants.VENDOR_ASSESSMENT_STATUS),
  getFormatedStatus(Constants.VENDOR_APPROVED_STATUS),
  getFormatedStatus(Constants.AWAITING_BUSINESS_REVIEW),
  getFormatedStatus(Constants.NEED_MORE_INFORMATION_STATUS),
]

//TODO: Workflow status maps for archived/onhold vendors based on previousState
// Level 4: TASTING_PANEL workflow statuses 
export const tastingPanelWorkflowStatusMap: string[] = [
  "va_tasting_panel_ask_nfp_document",
  "va_tasting_panel_nfp_requested",
  "va_tasting_panel_nfp_document_received", 
  "va_tasting_panel_in_review",
  "va_tasting_panel_nfp_document_revision",
  "va_tasting_panel_pre_panel_data_revision"
]

// Level 3: PREPANEL_AND_BUSINESS_REVIEW workflow statuses (includes higher levels)
export const prePanelAndBusinessReviewWorkflowStatusMap: string[] = [
  ...tastingPanelWorkflowStatusMap,
  "va_approved",
  "va_in_nt_review", 
  "va_in_cm_review",
  "va_prepanel_approved",
  "va_in_business_review",
  "va_business_review_in_revision"
]

// Level 2: VENDOR_INFORMATION workflow statuses (includes higher levels)  
export const vendorInformationWorkflowStatusMap: string[] = [
  ...prePanelAndBusinessReviewWorkflowStatusMap,
  "va_in_progress",
  "va_pre_assessment_tasks", 
  "va_in_review"
]

// Level 1: PRODUCT_INFORMATION workflow statuses (includes all levels)
export const productInformationWorkflowStatusMap: string[] = [
  ...vendorInformationWorkflowStatusMap,
  "new_idea_in_progress"
]

export const toCamelCase = (str: string) => {
  return str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

export const generateTemporaryId = () => {
  return Math.random().toString(36).substring(2, 9); // Simple unique ID
};

// Universal empty value checker
export const isValueEmpty = (value: unknown): boolean => {
  // Handle primitives
  if (value === null || value === undefined) return true;

  // Handle strings
  if (typeof value === "string") return !value.trim();

  // Handle numbers/booleans (considered non-empty)
  if (typeof value === "number" || typeof value === "boolean") return false;

  // Handle arrays
  if (Array.isArray(value)) return value.length === 0;

  // Handle objects (including empty objects)
  if (typeof value === "object") return Object.keys(value).length === 0;

  // Fallback for other types
  return false;
};

// Removed old status bar functions: getAssessmentStep and verifyAssessmentStep
// These are no longer needed with the new UnifiedStatusBar system

export const handleVendorAssessmentDelete = async (
  vendorAssessmentId: string, 
  setIsLoading: (value: boolean) => void, 
  messagesTexts: any, 
  closeConfirmationModal: () => void,
  router: { push: (path: string) => void },
  ListUrl: string
): Promise<void> => {
  setIsLoading(true);
  try {
    const resp = await VendorService.deleteVendorAssessment(vendorAssessmentId);
    if (resp.status === 200) {
      showToast({
        type: "success",
        message: messagesTexts?.TOAST_MESSAGES?.CP_VA_API_DELETE_VENDOR_SUCCESS || "Vendor Assessment deleted successfully",
      });
      router.push(ListUrl);
    }
  } catch (error) {
    handleApiError(
      error,
      messagesTexts?.TOAST_MESSAGES?.CP_VA_API_DELETE_VENDOR_FAIL || "Error deleting Vendor Assessment."
    );
  } finally {
    closeConfirmationModal();
    setIsLoading(false);
  }
};
export const prepareHtmlForEmail = (rawHtml: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<html><body>${rawHtml}</body></html>`, 'text/html');

  // Replace date inputs with values
  const dateInputs = doc.querySelectorAll('span[data-custom-date="datetime"]');
  dateInputs.forEach(input => {
    const inputEl = input as HTMLInputElement;
    const value = inputEl.value || input.getAttribute('value') || '';
    const jsDate = new Date(value);
    const formattedDate = dayjs(jsDate).format("dddd, DD MMM YYYY, h:mm A");
    const span = document.createElement("span");
    span.textContent = formattedDate;
    span.style.fontWeight = "bold";
    span.style.fontFamily = "Arial, sans-serif";
    span.style.fontSize = "18px";
    span.style.lineHeight = "32px";
    span.style.letterSpacing = "0%";
    input.parentNode?.replaceChild(span, input);
  });

  // Replace editable text field nodes with actual text values
  const editableTextFields = doc.querySelectorAll('span[data-editable-text-field="true"]');
  editableTextFields.forEach(field => {
    const value = field.getAttribute('value') || '';
    const textNode = document.createTextNode(value);
    field.parentNode?.replaceChild(textNode, field);
  });

   // Replace TJ logo image src with absolute URL for email compatibility.
   // IMPORTANT: Do NOT use `/_next/static/media/...` in emails because those hashed build assets can change across deployments,
   // breaking old emails. Put the logo in `/public/email/TjEmailLogo.png` (or host on a CDN) and use that stable URL.
   const tjLogoImg = doc.querySelector('img[id="tj_logo"]');
   if (tjLogoImg) {
     const imgElement = tjLogoImg as HTMLImageElement;
     // Email clients need absolute URLs, not relative paths
     const baseUrl = process.env.NEXT_PUBLIC_APP_URL || window.location.origin || '';
     imgElement.src = `${baseUrl}/email/TjEmailLogo.png`;
   }

  // Create HTML structure
  let html = '<!DOCTYPE html>'+
  '<html>'+
  '<head>'+
  '<meta charset="UTF-8"/>'+
  '<meta name="viewport" content="width=device-width, initial-scale=1.0"/>'+
  '<title>Welcome Email</title>'+
  '<style>*{font-family: Arial;}</style>'+
  '</head>'+
  '<body>'+
  doc.body.innerHTML +
  '</body>'+
  '</html>';
  return html;
};
// Utility to generate template from string with {} placeholders
export function generateTemplate(str: string) {
  const parts = str.split(/(\{l\})/);
  return parts.map(part =>
    part === '{l}' ? { type: 'input' as const } : { type: 'text' as const, value: part }
  );
}

/**
 * Ensures a filename has the .pdf extension
 * @param fileName - The filename to check and modify
 * @returns The filename with .pdf extension (case-insensitive check)
 */
export const ensurePdfExtension = (fileName: string): string => {
  if (!fileName) return fileName;
  return fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;
};

export const handleDownloadDocument = async (documentId: string, filename?: string, validationMessages?: any) => {
  try {
    await FileService.downloadFile(documentId, filename);
    console.log('PDF downloaded successfully');
  } catch (error) {
    console.error('Error downloading PDF:', error);
    handleApiError(error, validationMessages?.FAILED_DOWNLOAD_PDF_ERROR || 'Failed to download PDF');
  }
}

export const handleViewDocument = async (documentId: string, validationMessages?: any) => {
  try {
    const blob = await FileService.getFileContent(documentId);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');      
  } catch (error) {
    console.error('Error viewing PDF:', error);
    handleApiError(error, validationMessages?.FAILED_OPEN_PDF_ERROR || 'Failed to open PDF');
  }
}

export const toNumberIfPossible = (val: any) => (isNaN(Number(val)) ? val : Number(val));

/**
 * Rounds a numeric value represented as a string to a fixed number of decimals.
 * - Returns "" for empty input
 * - Preserves original string for non-numeric input
 */
export const roundNumericString = (raw: unknown, decimals: number = 2): string => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toFixed(decimals);
};

export const formatDecimalMaxTwo = (value: number | string | undefined | null): string => {
  // Handle undefined, null, or empty string
  if (value === undefined || value === null || value === '') {
    return '0';
  }
  
  // Convert to number for validation
  const numValue = Number(value);
  
  // Handle NaN or invalid numbers
  if (isNaN(numValue)) {
    return '0';
  }
  
  // Work with the original value as string if it's already a string to preserve precision
  // Otherwise convert number to string (may have floating point precision issues)
  const strValue = typeof value === 'string' ? value : String(numValue);
  
  // Check if string representation has decimal part
  if (strValue.includes('.')) {
    const parts = strValue.split('.');
    const integerPart = parts?.[0] ?? '0';
    let decimalPart = parts[1] || '';
    
    // Truncate to max 2 decimal places (not rounding)
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    // Remove trailing zeros from decimal part
    decimalPart = decimalPart.replace(/0+$/, '');
    
    // If decimal part is empty after removing zeros, return integer only
    if (decimalPart === '') {
      return integerPart ?? '0';
    }
    
    return `${integerPart}.${decimalPart}`;
  }
  
  // No decimal part, return as is (no .00 added)
  return strValue;
};

// Normalizes a SingleInputDropdown selection into a standardized format
// into a normalized shape: { option: { label, value, template }, inputs: [] }
// Replaces '(1)' placeholders with '{}' in the label to align with UI expectations.
function normalizeSingleInputSelection(sel: any): { option: { label: string; value: any; template: any[] }; inputs: any[] } | null {
  if (!sel) return null;
  const optionSrc: any = sel.option || sel;
  const labelRaw = optionSrc?.label ?? sel?.label ?? optionSrc?.value ?? sel?.value ?? '';
  const value = optionSrc?.value ?? sel?.value ?? labelRaw;
  const template = optionSrc?.template ?? sel?.template ?? [];
  const inputs = Array.isArray(sel?.inputs) ? sel.inputs : [];
  const label = String(labelRaw).replace(/\(1\)/g, '{}');
  return { option: { label, value, template }, inputs } as any;
}

// Parses all SingleInputDropdown selections from a raw value
function parseAllSingleInputSelections(raw: unknown): Array<{ option: { label: string; value: any; template: any[] }; inputs: any[] }> {
  if (raw == null || raw === '') return [];

  if (typeof raw === 'object') {
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.map(normalizeSingleInputSelection).filter(Boolean) as Array<{ option: { label: string; value: any; template: any[] }; inputs: any[] }>;
  }

  const str = String(raw);
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed.map(normalizeSingleInputSelection).filter(Boolean) as Array<{ option: { label: string; value: any; template: any[] }; inputs: any[] }>;
    }
    if (parsed && typeof parsed === 'object') {
      const one = normalizeSingleInputSelection(parsed);
      return one ? [one] : [];
    }
    if (typeof parsed === 'string') {
      const one = normalizeSingleInputSelection({ option: { label: parsed, value: parsed, template: [] }, inputs: [] });
      return one ? [one] : [];
    }
  } catch { /* not json; treat as plain label */ }

  const one = normalizeSingleInputSelection({
    option: { label: str.replace(/\(1\)/g, '{}'), value: str.replace(/\(1\)/g, '{}'), template: [] },
    inputs: [],
  });
  return one ? [one] : [];
}

// Parses a SingleInputDropdown selection from a raw value
export function parseSingleInputSelection(raw: any): { option: { label: string; value: any; template: any[] }; inputs: any[] } | null {
  const selections = parseAllSingleInputSelections(raw);
  return selections[0] ?? null;
}

// Checks if a value has the shape of a SingleInputDropdown selection
function hasSingleInputSelectionShape(value: unknown): boolean {
  if (value == null || value === "") return false;

  const isStructuredObject = (obj: unknown): boolean =>
    obj != null && typeof obj === "object" && "option" in (obj as object);

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.length > 0 && isStructuredObject(value[0]);
    }
    return isStructuredObject(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return false;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.length > 0 && isStructuredObject(parsed[0]);
      }
      return isStructuredObject(parsed);
    } catch {
      return false;
    }
  }

  return false;
}

/** Formats a parsed SingleInputDropdown selection for display (template + inputs, or `{}` / `{l}` in label). */
export function formatParsedSingleInputSelection(sel: any): string {
  if (!sel) return "";

  const template = Array.isArray(sel?.option?.template) ? sel.option.template : [];
  const inputs = Array.isArray(sel?.inputs) ? sel.inputs : [];
  const nonEmptyInputs = inputs.filter(
    (v: unknown) => v != null && String(v).trim() !== ""
  );

  let built = "";

  if (template.length > 0) {
    let inputOnlyIdx = 0;

    template.forEach((part: { type?: string; value?: string }, partIdx: number) => {
      if (part?.type === "text") {
        built += String(part?.value ?? "");
      } else if (part?.type === "input") {
        const byPartIdx = inputs[partIdx];
        const byInputOnly = inputs[inputOnlyIdx];
        const value =
          byPartIdx != null && String(byPartIdx).trim() !== ""
            ? byPartIdx
            : byInputOnly != null && String(byInputOnly).trim() !== ""
              ? byInputOnly
              : (nonEmptyInputs[inputOnlyIdx] ?? "");
        built += String(value);
        inputOnlyIdx += 1;
      }
    });
  } else {
    built = sel?.option?.label || sel?.option?.value || "";
    nonEmptyInputs.forEach((inputValue: unknown) => {
      built = built.replace("{}", String(inputValue));
      built = built.replace("{l}", String(inputValue));
    });
  }

  // Nutrition supporting statements use a leading * as a mandatory marker in dropdown labels
  return built.replace(/^\*\s?/, "").trim();
}

/** Formats audit log / API raw values that match SingleInput JSON shape; passes others through unchanged. */
export function formatSingleInputSelectionForDisplay(raw: unknown): unknown {
  if (raw == null || raw === "") return raw;
  if (!hasSingleInputSelectionShape(raw)) return raw;

  const selections = parseAllSingleInputSelections(raw);
  if (!selections.length) return raw;

  return selections
    .map(formatParsedSingleInputSelection)
    .filter((formatted) => formatted.length > 0)
    .join(", ");
}

/**
 * Generates artwork project ID: `vendorId-sku-000-{versionSuffix}`.
 * - Missing / empty `versionName` → suffix **`V1`**.
 * - Otherwise appends **`versionName` trimmed and uppercased** (e.g. `v1` / `V1` → `V1`, `v100` → `V100`). Intended for short version tokens only (`v1`, `V2`, …).
 * @param vendorId - The vendor number/ID
 * @param sku - The SKU number
 * @param versionName - Optional API version label
 * @returns Generated artwork project ID string or empty string if vendorId/sku are missing
 */
export function generateArtworkProjectId(
  vendorId: string,
  sku: string,
  versionName?: string
): string {
  if (!vendorId || !sku) {
    return '';
  }
  const base = `${vendorId}-${sku}-000`;
  const raw = String(versionName ?? "").trim();
  const suffix = raw === "" ? "V1" : raw.toUpperCase();
  return `${base}-${suffix}`;
}

/**
 * Preserves multipack values (giftPack, amountOfProductsInGiftPack) from existing idea data
 * when updating a product idea. These are product-level fields that should not be overwritten
 * when saving product information or adding new vendors.
 * 
 * @param ideaId - The ID of the product idea (string or number)
 * @param ideaData - The idea data object to update (will be mutated with preserved multipack values)
 * @returns Promise that resolves when multipack values are preserved (or fetch fails gracefully)
 */
export async function preserveMultipackValues(
  ideaId: string | number | undefined,
  ideaData: Record<string, any>
): Promise<void> {
  // Only proceed if ideaId exists (updating existing idea, not creating new one)
  if (!ideaId) {
    return;
  }

  try {
    // Fetch current idea data to get existing multipack values
    const currentIdeaData = await ProductService.getIdea(Number(ideaId));
    
    // Preserve multipack values from existing idea data if they exist
    if (currentIdeaData) {
      if (currentIdeaData.giftPack !== undefined) {
        ideaData.giftPack = currentIdeaData.giftPack;
      }
      if (
        currentIdeaData.amountOfProductsInGiftPack !== undefined &&
        currentIdeaData.amountOfProductsInGiftPack !== null
      ) {
        ideaData.amountOfProductsInGiftPack = currentIdeaData.amountOfProductsInGiftPack;
      }
    }
  } catch (fetchError) {
    // Log warning but don't block the save operation
    console.warn(
      "Could not fetch current idea data, proceeding without multipack preservation:",
      fetchError
    );
  }
}

const sanitizeErrorMessage = (value: any): string | undefined => {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

// Gracefully goes to latter condition if cannot find former condition 
export const extractErrorMessage = (error: any): string | undefined => {
  const errorMessage = 
    sanitizeErrorMessage(error?.response?.data?.errorMessage) || 
    sanitizeErrorMessage(error?.response?.data?.message) || 
    sanitizeErrorMessage(error?.response?.data?.error) || 
    sanitizeErrorMessage(error?.message) ||
    sanitizeErrorMessage(error);
  
  return errorMessage;
};

export const isTimestampMatch = (
  timestamp1: number | string,
  timestamp2: number | string,
  tolerance = 1 // ±1second
): boolean => {
  return Math.abs(Number(timestamp1) - Number(timestamp2)) <= tolerance;
};

type VendorUserIdRecord = Record<string, unknown>;

function firstNonEmptyVendorUserId(candidates: unknown[]): string {
  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (!value || value.startsWith("temp-")) continue;
    return value;
  }
  return "";
}

/** Vendor user id from IAM list `objects[]` item. */
export function resolveVendorUserListRecordId(user: VendorUserIdRecord): string {
  return firstNonEmptyVendorUserId([user.vendorUserId, user.id, user.userId]);
}

/** Vendor user id from a grid / infinite-scroll row. */
export function resolveVendorUserRowId(row: VendorUserIdRecord): string {
  return firstNonEmptyVendorUserId([row.__nodeId, row.vendorUserId, row.id, row.userId]);
}

/** Vendor user id from `GET /vendorUserManagement/detail/{id}` response. */
export function resolveVendorUserEditResponseId(response: VendorUserIdRecord): string {
  return firstNonEmptyVendorUserId([response.vendorUserId, response.id, response.userId]);
}


// If value can be converted to a number, format integer with US grouping; leave decimals unchanged.
export const formatNumberToUSNumberFormat = (
  value: string | number | null | undefined
): string | number | null | undefined => {
  if (value == null) return "";
  if (typeof value === "string" && value.trim() === "") return "";
  if (!isNaN(Number(value))) {
    const normalized = String(value).replace(/,/g, '');
    const [integerPart, decimalPart] = normalized.split('.');
    const formattedInteger = Number(integerPart || 0).toLocaleString('en-US');
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  }
  return value;
};